import { useState, useRef, useCallback } from 'react';

interface DownloadProgress {
  status: 'starting' | 'downloading' | 'finished' | 'completed' | 'error';
  percent: number;
  downloaded_mb?: number;
  total_mb?: number;
  speed_mb?: number;
  eta?: number;
  message: string;
  download_url?: string;
  filename?: string;
  file_size?: number;
}

interface UseYouTubeDownloadProgressReturn {
  progress: DownloadProgress;
  connectToDownload: (downloadId: string) => void;
  disconnect: () => void;
  isConnected: boolean;
}

export const useYouTubeDownloadProgress = (): UseYouTubeDownloadProgressReturn => {
  const [progress, setProgress] = useState<DownloadProgress>({
    status: 'starting',
    percent: 0,
    message: 'Preparing download...'
  });
  const [isConnected, setIsConnected] = useState(false);
  const websocketRef = useRef<WebSocket | null>(null);

  const disconnect = useCallback(() => {
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
      setIsConnected(false);
    }
  }, []);

  const connectToDownload = useCallback((downloadId: string) => {
    // Clean up any existing connection
    disconnect();

    try {
      // Get WebSocket URL
      const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
      
      let wsUrl: string;
      if (baseUrl.includes('stream-clipperz.web.app') || baseUrl.includes('clipthis.io') || baseUrl === window.location.origin) {
        // Production environment - connect to Cloud Run
        wsUrl = `wss://streamclipper-backend-rnf5px4hsq-uc.a.run.app/api/v1/tools/download/progress/${downloadId}`;
      } else {
        // Local development
        wsUrl = baseUrl.replace('http://', 'ws://').replace('https://', 'wss://') + `/api/v1/tools/download/progress/${downloadId}`;
      }
      
      // console.log('🔌 Connecting to YouTube download progress WebSocket:', wsUrl);
      const ws = new WebSocket(wsUrl);
      websocketRef.current = ws;

      ws.onopen = () => {
        // console.log('✅ YouTube download progress WebSocket connected to:', wsUrl);
        setIsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // console.log('📡 Received WebSocket message:', data);
          
          if (data.type === 'progress' && data.progress) {
            setProgress(data.progress);
            
            // If download is finished or completed, we need to trigger completion handling
            if (data.progress.status === 'finished' || data.progress.status === 'completed') {
              // console.log('🎉 Download completed, closing WebSocket');
              // Close WebSocket connection since download is complete
              setTimeout(() => {
                ws.close();
              }, 1000); // Small delay to ensure message is processed
            }
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error, 'Raw data:', event.data);
        }
      };

      ws.onclose = (_event) => {
        // console.log('📡 YouTube download progress WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        websocketRef.current = null;
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setIsConnected(false);
        setProgress(prev => ({
          ...prev,
          status: 'error',
          message: 'Connection error - please try again'
        }));
      };

    } catch (error) {
      console.error('Failed to connect to YouTube download progress WebSocket:', error);
    }
  }, [disconnect]);

  return {
    progress,
    connectToDownload,
    disconnect,
    isConnected
  };
};
